/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



import React, { useMemo } from 'react';
import { Chip, TableCell } from '@mui/material';
import { useTranslation } from 'react-i18next';
import DataTable from 'dashboard-template/examples/Tables/DataTable';
import DefaultCell from 'components/generals/DefaultCell';
import { ActivityLogInterface } from 'store/slices/activity-log.slice';
import { TableProps } from 'interfaces';
import { ActionLogColors } from 'types/form.enum';
import { getFromISOISOYYYYMMDDTHHMMSSTimeFormat } from 'utils/date.utils';

/**
 * Activity log table component for displaying system audit trail.
 * 
 * Features:
 * - Displays user activity logs with color-coded action types
 * - Columns: timestamp, username, person name, action, entity type, IP address
 * - Color-coded action chips for visual distinction
 * - Search functionality enabled
 * - Formatted timestamp display (YYYY-MM-DD HH:MM:SS)
 * - Safe handling of missing user/person data
 * 
 * Action Color Coding:
 * - CREATE: success (green)
 * - UPDATE/EDIT: warning (orange)
 * - DELETE: error (red)
 * - LOGIN: info (blue)
 * - LOGOUT: default (gray)
 * - FAILED_LOGIN: error (red)
 * - Other: default (gray)
 * 
 * Security & Compliance:
 * - Complete audit trail for regulatory compliance
 * - Tracks user actions across all entities
 * - IP address logging for security monitoring
 * - Timestamp precision for forensic analysis
 * 
 * Use Cases:
 * - Security auditing
 * - Compliance reporting
 * - User activity monitoring
 * - Troubleshooting user actions
 * - Forensic investigation
 * 
 * @param {TableProps<ActivityLogInterface>} props - Table properties
 * @param {ActivityLogInterface[]} props.modelList - Array of activity log records
 * 
 * @returns {JSX.Element} Activity log table with color-coded actions
 */
const ActivityLogTable: React.FC<TableProps<ActivityLogInterface>> = (props) => {
  const { t } = useTranslation();

  /**
   * Determines color for action chip based on action type.
   * 
   * @param {string} action - Action type string
   * @returns {string} Material-UI color name
   */
  const getActionColor = (action: string) => {
    if (action.includes(ActionLogColors.CREATE)) return 'success';
    if (action.includes(ActionLogColors.UPDATE) || action.includes(ActionLogColors.EDIT)) return 'warning';
    if (action.includes(ActionLogColors.DELETE)) return 'error';
    if (action === ActionLogColors.LOGIN) return 'info';
    if (action === ActionLogColors.LOGOUT) return 'default';
    if (action === ActionLogColors.FAILED_LOGIN) return 'error';
    return 'default';
  };

  const getUITable = (list: ActivityLogInterface[]) => {
    return {
      columns: [
        {
          Header: t('activityLogs.timestamp'),
          accessor: 'createdAt',
          Cell: ({ value }: { value: string }) => (
            <DefaultCell value={getFromISOISOYYYYMMDDTHHMMSSTimeFormat(new Date(value))} />
          ),
        },
        {
          Header: t('activityLogs.user'),
          accessor: 'user.username',
          Cell: ({ value }: { value: string }) => (
            <DefaultCell value={value || 'Unknown'} />
          ),
        },
        {
          Header: t('activityLogs.personName'),
          accessor: 'person',
          Cell: ({ value }: { value: any }) => {
            const personName = value?.name && value?.lastname 
              ? `${value.name} ${value.lastname}`.trim()
              : '-';
            return <DefaultCell value={personName} />;
          },
        },
        {
          Header: t('activityLogs.action'),
          accessor: 'action',
          Cell: ({ value }: { value: string }) => (
            <Chip
              label={value}
              color={getActionColor(value) as any}
              size="small"
            />
          ),
        },
        {
          Header: t('activityLogs.entityType'),
          accessor: 'entityType',
          Cell: ({ value }: { value: string }) => (
            <DefaultCell value={value || '-'} />
          ),
        },
        {
          Header: t('activityLogs.ipAddress'),
          accessor: 'ipAddress',
          Cell: ({ value }: { value: string }) => (
            <DefaultCell value={value || '-'} />
          ),
        },
      ],
      rows: list,
    };
  };

  const dataTableData = useMemo(() => getUITable(props.modelList), [props.modelList]);

  return (
    <DataTable 
      {...props} 
      table={dataTableData} 
      canSearch 
      isLoading={props.isLoading}
    />
  );
};

export default ActivityLogTable;
